// Generated from scripts/dialog.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';
export class DialogOpenEvent extends CustomEvent {
    constructor() {
        super(DialogOpenEvent.eventName);
    }
    static eventName = 'dialog:open';
}
/**
 * Keeps the focus ring that opening a dialog draws for a keyboard user only. `show()` and
 * `showModal()` focus the dialog's first control, and iOS Safari rings every scripted focus because
 * a tapped button never takes focus; the opener tells the two apart, since a keyboard user's opener
 * showed its ring. A focus() on the focused element changes nothing, so the button lets go first. A
 * text field is left alone: it always shows its ring, and refocusing it could drop the keyboard.
 *
 * @param openerShowedRing - Whether the opener matched `:focus-visible`, read before opening.
 */
export function matchOpenerFocusRing(openerShowedRing) {
    const focused = document.activeElement;
    if (openerShowedRing || !(focused instanceof HTMLButtonElement) || !focused.matches(':focus-visible'))
        return;
    focused.blur();
    focused.focus({ focusVisible: false, preventScroll: true });
}
export class DialogCloseEvent extends CustomEvent {
    constructor() {
        super(DialogCloseEvent.eventName);
    }
    static eventName = 'dialog:close';
}
/**
 * Host for a native modal `<dialog>`: scroll lock, closing on a click outside or Escape, a closing
 * animation and focus return to the control that opened it. Dispatches {@link DialogOpenEvent} and
 * {@link DialogCloseEvent} on itself.
 *
 * `showDialog()` does all of its work in the task that called it. iOS shows the on-screen keyboard
 * for a programmatic focus only inside the user-activation task, so a dialog that opens in a later
 * frame, timer or promise cannot raise the keyboard for a field it focuses.
 *
 * Refs: `dialog` is the `<dialog>` element. The `dialog-active-min-width` and
 * `dialog-active-max-width` attributes close the dialog when the window leaves that range. The
 * `open-on-load` attribute opens the dialog once, as soon as the host connects: the server sets it
 * when the page has to come back with the dialog open, such as the password page after a wrong
 * password.
 */
export class DialogComponent extends Component {
    requiredRefs = ['dialog'];
    #previousScrollY = 0;
    /** The control that had focus when the dialog opened, so closing can return focus to it. */
    #opener = null;
    /** When the dialog last opened, on the clock `Event.timeStamp` uses. */
    #openedAt = 0;
    #closing = false;
    get #dialog() {
        const { dialog } = this.refs;
        if (!(dialog instanceof HTMLDialogElement))
            throw new Error('dialog-component needs ref="dialog" on a <dialog>.');
        return dialog;
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.minWidth || this.maxWidth) {
            window.addEventListener('resize', this.#handleResize);
        }
        // Once: a host that is moved or morphed connects again and must not reopen.
        if (this.hasAttribute('open-on-load')) {
            this.removeAttribute('open-on-load');
            this.showDialog();
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.minWidth || this.maxWidth) {
            window.removeEventListener('resize', this.#handleResize);
        }
        const { dialog } = this.refs;
        if (dialog instanceof Element)
            unlockScroll(dialog);
    }
    #handleResize = debounce(() => {
        const { minWidth, maxWidth } = this;
        if (!minWidth && !maxWidth)
            return;
        const windowWidth = window.innerWidth;
        if (windowWidth < minWidth || windowWidth > maxWidth) {
            void this.closeDialog();
        }
    }, 50);
    /** Shows the dialog. */
    showDialog() {
        const dialog = this.#dialog;
        if (dialog.open)
            return;
        // One read, then writes: no layout thrash, so there is nothing to wait a frame for.
        this.#previousScrollY = getScrollTop();
        const { activeElement } = document;
        this.#opener = activeElement instanceof HTMLElement && activeElement !== document.body ? activeElement : null;
        const openerShowedRing = this.#opener?.matches(':focus-visible') ?? false;
        lockScroll(dialog);
        this.#openedAt = performance.now();
        dialog.showModal();
        matchOpenerFocusRing(openerShowedRing);
        this.dispatchEvent(new DialogOpenEvent());
        this.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeyDown);
    }
    /** Closes the dialog after its closing animation. */
    closeDialog = async () => {
        const dialog = this.#dialog;
        if (!dialog.open || this.#closing)
            return;
        this.#closing = true;
        this.removeEventListener('click', this.#handleClick);
        this.removeEventListener('keydown', this.#handleKeyDown);
        // Drop any running animation and force a reflow so the closing animation starts from its first frame.
        dialog.style.animation = 'none';
        void dialog.offsetWidth;
        dialog.classList.add('dialog-closing');
        dialog.style.animation = '';
        await onAnimationEnd(dialog, undefined, {
            subtree: false,
        });
        unlockScroll(dialog);
        scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
        dialog.close();
        dialog.classList.remove('dialog-closing');
        this.#restoreFocus();
        this.#closing = false;
        this.dispatchEvent(new DialogCloseEvent());
    };
    /** Opens the dialog when it is closed and closes it when it is open. */
    toggleDialog = () => {
        if (this.#dialog.open) {
            void this.closeDialog();
        }
        else {
            this.showDialog();
        }
    };
    /**
     * Returns focus to the control that opened the dialog. When that control is gone or hidden, as
     * after a menu drawer hands over to the dialog and closes, focus goes to the first visible control
     * whose `aria-controls` names this dialog.
     */
    #restoreFocus() {
        const opener = this.#opener;
        this.#opener = null;
        if (opener && this.#focus(opener))
            return;
        if (!this.id)
            return;
        for (const control of document.querySelectorAll(`[aria-controls="${CSS.escape(this.id)}"]`)) {
            if (this.#focus(control))
                return;
        }
    }
    /** Focuses a control without scrolling and reports whether it took focus; hidden or detached controls cannot. */
    #focus(element) {
        if (!element.isConnected || this.contains(element))
            return false;
        element.focus({ preventScroll: true });
        return document.activeElement === element;
    }
    /**
     * Closes the dialog when the user clicks outside of it. The click that opened the dialog is still
     * travelling toward this element when the listener is added, and a trigger inside the host lies
     * outside the dialog, so clicks that began before the open are ignored.
     */
    #handleClick = (event) => {
        if (event.timeStamp <= this.#openedAt)
            return;
        // A handler inside the dialog can replace the node that was clicked before this listener sees the click (a
        // table rebuilt by the control that was pressed). The node is detached by then, so it no longer lies inside the
        // dialog, but the path the event took was fixed when it was dispatched and still passes through the dialog. A
        // click on the backdrop has the dialog itself as its target and is judged by where it landed.
        if (event.target !== this.#dialog && event.composedPath().includes(this.#dialog))
            return;
        if (isClickedOutside(event, this.#dialog)) {
            void this.closeDialog();
        }
    };
    /** Closes the dialog when the user presses the escape key. */
    #handleKeyDown = (event) => {
        if (event.key !== 'Escape')
            return;
        event.preventDefault();
        void this.closeDialog();
    };
    /** The minimum window width at which the dialog stays open. */
    get minWidth() {
        return Number(this.getAttribute('dialog-active-min-width'));
    }
    /** The maximum window width at which the dialog stays open. */
    get maxWidth() {
        return Number(this.getAttribute('dialog-active-max-width'));
    }
}
if (!customElements.get('dialog-component'))
    customElements.define('dialog-component', DialogComponent);
document.addEventListener('toggle', (event) => {
    if (event.target instanceof HTMLDetailsElement && event.target.hasAttribute('scroll-lock')) {
        if (event.target.open) {
            lockScroll(event.target);
        }
        else {
            unlockScroll(event.target);
        }
    }
}, { capture: true });
